import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import toast from "react-hot-toast";
import {
  useCreateEnquiryMutation,
  useResubmitEnquiryMutation,
  useCreateConfirmedOrderMutation,
  useGetEnquiryQuery,
} from "../../salesApi";
import GuardRequirementsEditor, {
  computeGuardsCount,
  computeEarliestStartDate,
  toGuardRequirementRequests,
} from "../../components/GuardRequirementsEditor";
import CustomerPicker from "../../components/CustomerPicker";
import DiscountFields, { TotalsSummary, computeDiscountedTotals, discountRequest } from "../../components/DiscountFields";
import ServiceLinesGrid, {
  linesStateFrom,
  checkedServiceTypeIds,
  computeLinesSubTotal,
  alarmsQtyFrom,
  hasCheckedKind,
  toServiceLineRequests,
  validateServiceLines,
} from "../../components/ServiceLinesGrid";
import SalespersonPicker from "../../components/SalespersonPicker";
import { useListQuotationsQuery, useSetQuotationSalesLinkMutation } from "../../quotations/quotationsApi";
import { useCanViewQuotations, useCanEditQuotation } from "../../quotations/useQuotationPermissions";
import { QUOTATION_STATUS_LABEL } from "../../quotations/quotationStatusMaps";
import { useCanBookForOthers } from "../../useSalesPermissions";
import { useGetServiceTypesQuery } from "../../../masters/serviceTypes/serviceTypesApi";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useClaims } from "../../../auth/useClaims";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";
import { DEFAULT_VAT_PERCENT } from "../../../../components/common/vatDefaults";

const TITLE_BY_MODE = {
  new: "New Sales Enquiry",
  resubmit: "Resubmit Sales Enquiry",
  directConfirm: "Direct Confirmed Order",
};

// One form for Create, Resubmit, and Direct Confirmed Order (mode prop) -
// mirrors Flutter's own create_enquiry_screen.dart, which serves all three
// from one widget, rather than three near-duplicate forms.
// Services are a priced grid (Batch164): tick a service, enter Qty x Rate. On
// an enquiry the rates are the salesperson's proposal (a line may be left
// unpriced; the approver can adjust). A Direct Confirmed Order skips approval,
// so every ticked line must be priced and VAT / discount are entered here too.
const EnquiryForm = ({ mode = "new" }) => {
  const navigate = useNavigate();
  const { recordGuid } = useParams();
  const isResubmit = mode === "resubmit";
  const isDirectConfirm = mode === "directConfirm";

  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  // The header's branch picker drives this form's own Branch field. Without it
  // the field opened on "--Select--" and stayed there, and everything below that
  // depends on a branch - above all the service catalog - had nothing to load.
  const { selectedBranchId } = useBranchFilter();
  const [createEnquiry, { isLoading: isCreating }] = useCreateEnquiryMutation();
  // Booking FOR someone else (Batch148). Optional - left blank it is "my own",
  // exactly as before. Gated because the salesperson is who the COMMISSION is
  // paid to, so this moves real money to a different person.
  const canBookForOthers = useCanBookForOthers();
  // The employee the sale belongs to (Batch152). Holds the whole react-select
  // option, not just the guid, so the chosen person stays visible in the box.
  const [salespersonEmployee, setSalespersonEmployee] = useState(null);
  const [resubmitEnquiry, { isLoading: isResubmitting }] = useResubmitEnquiryMutation();
  const [createConfirmedOrder, { isLoading: isConfirming }] = useCreateConfirmedOrderMutation();
  const { data: existingDetail } = useGetEnquiryQuery(recordGuid, { skip: !isResubmit });

  const availableBranches = isAllBranches
    ? branches
    : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  // Empty to start with on purpose. This used to try to default to the caller's
  // only branch here, but useState's initial value is evaluated on the FIRST
  // render - before /admin/branches has answered - so availableBranches was
  // always empty and the default never happened. For a single-branch user the
  // Branch select is also disabled, so they were left on "--Select--" with no
  // way out and an empty service catalog. Both cases are settled by the effect
  // below, which runs again once the branches arrive.
  const [branchId, setBranchId] = useState("");
  // A Direct Confirmed Order is a deal already done with a customer already on
  // the books, so it always links an existing customer - no toggle, no
  // free-text entry. Enquiries keep the choice: a prospect may not be a
  // customer yet (the API creates one on Confirm).
  const [isExistingCustomer, setIsExistingCustomer] = useState(isDirectConfirm);
  const [customerOption, setCustomerOption] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [area, setArea] = useState("");
  const [guardRows, setGuardRows] = useState([]);
  // { [serviceTypeId]: { checked, qty, rate } } - see ServiceLinesGrid.
  const [serviceLines, setServiceLines] = useState({});
  const [salespersonRemarks, setSalespersonRemarks] = useState("");
  const [vatRate, setVatRate] = useState(String(DEFAULT_VAT_PERCENT));
  // Direct Confirmed Order only (Batch163) - carried onto the order by Confirm.
  const [discountPercent, setDiscountPercent] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");

  // Quotation to link on save (Batch164). Only an existing customer can have
  // quotations, and linking needs Quotation.Edit (the server checks both).
  // Resubmit links from the enquiry's own page instead.
  const canViewQuotations = useCanViewQuotations();
  const canEditQuotation = useCanEditQuotation();
  const canLinkQuotation = canViewQuotations && canEditQuotation && !isResubmit;
  const [linkQuotationGuid, setLinkQuotationGuid] = useState("");
  const [setQuotationSalesLink] = useSetQuotationSalesLinkMutation();
  const pickedCustomer = isExistingCustomer ? customerOption?.customer : null;
  const { data: customerQuotations, isFetching: isFetchingQuotations } = useListQuotationsQuery(
    { custId: pickedCustomer?.custId, pageSize: 50 },
    { skip: !canLinkQuotation || !pickedCustomer?.custId }
  );
  const quotationOptions = (customerQuotations?.items || []).filter(
    (q) =>
      (q.status === "SUBMITTED" || q.status === "APPROVED") &&
      (q.customerBranchId == null || Number(q.customerBranchId) === Number(pickedCustomer?.branchId))
  );
  // A quotation belongs to one customer - a new customer drops the choice.
  useEffect(() => setLinkQuotationGuid(""), [pickedCustomer?.custId, pickedCustomer?.branchId]);

  // After the enquiry exists. A failed link does not undo the save - it says
  // so, and the link can be made from the saved record's Quotations panel.
  const linkPickedQuotation = async (enquiryRecordGuid) => {
    if (!linkQuotationGuid || !enquiryRecordGuid) return;
    try {
      await setQuotationSalesLink({ recordGuid: linkQuotationGuid, salesEnquiryRecordGuid: enquiryRecordGuid }).unwrap();
    } catch (e) {
      toast.error(`Saved, but the quotation could not be linked: ${e?.message || "server error"}. Link it from the Quotations panel.`);
    }
  };

  const { data: serviceTypeOptions = [], isError: serviceTypesFailed } = useGetServiceTypesQuery(
    { branchId: branchId ? Number(branchId) : undefined, isActive: true },
    { skip: !branchId }
  );

  // Service types are a per-branch catalog, so anything already chosen belongs
  // to the branch it was chosen under. Moving branch has to let those go rather
  // than submit a service id the new branch does not have.
  const changeBranch = (nextBranchId) => {
    if (String(nextBranchId) === String(branchId)) return;
    setBranchId(nextBranchId);
    setServiceLines({});
    setGuardRows((rows) => rows.map((row) => ({ ...row, serviceTypeId: "" })));
  };

  // Follow the header's branch picker. It is the app-wide "which branch am I
  // working in" control, and a form that ignored it left the user setting the
  // same thing twice - or, on a single-branch account whose select is disabled,
  // unable to set it at all.
  //
  // "All branches" (null) is not a branch an order can be raised against, so it
  // leaves the field alone rather than clearing a choice already made. Resubmit
  // is excluded outright: that form's branch comes from the enquiry being
  // resubmitted and is not the user's to change.
  useEffect(() => {
    if (isResubmit) return;
    if (selectedBranchId != null) changeBranch(selectedBranchId);
    else if (!branchId && availableBranches.length === 1) changeBranch(availableBranches[0].branchId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchId, isResubmit, availableBranches.length]);

  // Resubmit prefills from the rejected enquiry - branch, customer, guard lines
  // and the priced service lines all carry over for the salesperson to revise.
  useEffect(() => {
    if (!isResubmit || !existingDetail) return;
    const { enquiry, guardRequirements = [], serviceTypes = [], serviceLines: savedLines } = existingDetail;
    setBranchId(enquiry.branchId);
    if (enquiry.custId) {
      setIsExistingCustomer(true);
      setCustomerOption({ value: `${enquiry.branchId}-${enquiry.custId}`, label: enquiry.customerName, customer: { custId: enquiry.custId, branchId: enquiry.branchId } });
    } else {
      setCustomerName(enquiry.customerName || "");
      setCustomerPhone(enquiry.customerPhone || "");
      setCustomerEmail(enquiry.customerEmail || "");
      setCustomerAddress(enquiry.customerAddress || "");
    }
    setArea(enquiry.area || "");
    setGuardRows(
      guardRequirements.map((l) => ({
        key: `greq-${l.requirementId}`,
        serviceTypeId: String(l.serviceTypeId),
        deploymentType: l.deploymentType,
        shift: l.shift,
        schedule: l.schedule,
        startDate: l.startDate?.slice(0, 10) || "",
        endDate: l.endDate?.slice(0, 10) || "",
        qty: String(l.qty),
        rate: "",
        remarks: l.remarks || "",
      }))
    );
    // Older enquiries have no saved lines - tick their services, alarms at the old count.
    setServiceLines(
      savedLines?.length
        ? linesStateFrom(savedLines)
        : Object.fromEntries(serviceTypes.map((s) => [
            s.serviceTypeId,
            { checked: true, qty: s.serviceKind === "ALARMS" ? String(enquiry.alarmsCount || 1) : s.serviceKind === "OTHER" ? "1" : "", rate: "" },
          ]))
    );
    setSalespersonRemarks(enquiry.salespersonRemarks || "");
  }, [isResubmit, existingDetail]);

  // Guards are only sent while the Guards service is ticked; the rows stay in
  // state so un-ticking and re-ticking does not lose what was typed.
  const guardsTicked = hasCheckedKind(serviceTypeOptions, serviceLines, "GUARDS");
  const activeGuardRows = guardsTicked ? guardRows : [];
  const guardsCount = computeGuardsCount(activeGuardRows);
  // Guards start on the date entered against their line in the grid, so the
  // earliest of those IS the requested deployment date - no separate field.
  const deploymentDate = computeEarliestStartDate(activeGuardRows);

  const linesSubTotal = computeLinesSubTotal(serviceTypeOptions, serviceLines, guardsCount);
  const previewTotals = computeDiscountedTotals(linesSubTotal, vatRate, discountPercent, discountAmount);

  const buildBaseBody = () => ({
    custId: isExistingCustomer ? Number(customerOption?.customer?.custId) : undefined,
    customerBranchId: isExistingCustomer ? Number(customerOption?.customer?.branchId) : undefined,
    customerName: isExistingCustomer ? undefined : customerName.trim(),
    customerPhone: isExistingCustomer ? undefined : customerPhone.trim(),
    customerEmail: isExistingCustomer ? undefined : customerEmail.trim() || undefined,
    customerAddress: isExistingCustomer ? undefined : customerAddress.trim() || undefined,
    area: isExistingCustomer ? undefined : area.trim(),
    guardRequirements: toGuardRequirementRequests(activeGuardRows),
    // Both still sent for the request shape; the server takes them from serviceLines.
    alarmsCount: alarmsQtyFrom(serviceTypeOptions, serviceLines),
    serviceTypeIds: checkedServiceTypeIds(serviceLines).filter((id) => serviceTypeOptions.some((s) => s.serviceTypeId === id)),
    serviceLines: toServiceLineRequests(serviceTypeOptions, serviceLines, guardsCount),
    salespersonRemarks: salespersonRemarks.trim() || undefined,
    requestedDeploymentDate: guardsCount > 0 && deploymentDate ? deploymentDate : undefined,
  });

  const validate = () => {
    if (availableBranches.length > 1 && !branchId && !isResubmit) return "Branch is required";
    if (!isExistingCustomer && !customerName.trim()) return "Customer name is required";
    if (!isExistingCustomer && !customerPhone.trim()) return "Customer phone is required";
    if (isExistingCustomer && !customerOption) return "Search for and select the customer";
    if (!isExistingCustomer && !area.trim()) return "Area is required for a new customer";
    const linesError = validateServiceLines(serviceTypeOptions, serviceLines, guardsCount, { requireRates: isDirectConfirm });
    if (linesError) return linesError;
    if (activeGuardRows.some((r) => (Number(r.qty) || 0) > 0 && !r.startDate)) return "Enter a Start Date on every guard requirement line";
    if (activeGuardRows.some((r) => !r.serviceTypeId)) return "Pick the guard service on every guard requirement line";
    if (isDirectConfirm && (Number(vatRate) < 0)) return "VAT % cannot be negative";
    if (isDirectConfirm && previewTotals.exceeds) return "The discount is more than the sub total";
    return null;
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) return toast.error(error);
    try {
      if (isDirectConfirm) {
        const order = await createConfirmedOrder({
          branchId: branchId ? Number(branchId) : undefined,
          salespersonEmployeeRecordGuid: salespersonEmployee?.value || undefined,
          ...buildBaseBody(),
          vatRate: Number(vatRate) || 0,
          ...discountRequest(discountPercent, discountAmount),
        }).unwrap();
        await linkPickedQuotation(order.enquiryRecordGuid);
        toast.success("Order confirmed");
        navigate(paths.salesOrderDetail(order.recordGuid), { replace: true });
      } else if (isResubmit) {
        await resubmitEnquiry({
          recordGuid,
          ...buildBaseBody(),
        }).unwrap();
        toast.success("Enquiry resubmitted");
        navigate(paths.salesEnquiryDetail(recordGuid), { replace: true });
      } else {
        const enquiry = await createEnquiry({
          branchId: branchId ? Number(branchId) : undefined,
          salespersonEmployeeRecordGuid: salespersonEmployee?.value || undefined,
          ...buildBaseBody(),
        }).unwrap();
        await linkPickedQuotation(enquiry.recordGuid);
        toast.success("Enquiry submitted");
        navigate(paths.salesEnquiryDetail(enquiry.recordGuid), { replace: true });
      }
    } catch (err) {
      toast.error(err?.message || "Could not submit");
    }
  };

  const isSaving = isCreating || isResubmitting || isConfirming;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={isDirectConfirm ? paths.salesOrders : paths.salesEnquiries} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to {isDirectConfirm ? "Orders" : "Enquiries"}
              </Link>
              <h4>{TITLE_BY_MODE[mode]}</h4>
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Customer</h5>
            <div className="row">
              {!isResubmit && (
                <div className="col-md-4 mb-3">
                  <label className="form-label">Branch</label>
                  <select className="form-control" value={branchId} onChange={(e) => changeBranch(e.target.value)} disabled={availableBranches.length <= 1}>
                    <option value="">--Select--</option>
                    {availableBranches.map((b) => (
                      <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                    ))}
                  </select>
                  <small className="text-muted">Follows the branch picker in the header.</small>
                </div>
              )}
              {/* Entering a sale that belongs to another EMPLOYEE (Batch152) -
                  typically someone with no computer, or no access to this module.
                  An employee search, not a user list: the named person needs no
                  login at all. Optional; blank books it in your own name. You
                  stay recorded as the creator either way - only the commission
                  moves. Only shown to a Sales.BookForOthers holder. */}
              {!isResubmit && canBookForOthers && (
                <div className="col-md-4 mb-3">
                  <label className="form-label">Salesperson (employee)</label>
                  <SalespersonPicker value={salespersonEmployee} onChange={setSalespersonEmployee} />
                  <small className="text-muted">
                    {salespersonEmployee
                      ? `The sale and its commission belong to ${salespersonEmployee.label}. You stay recorded as the creator.`
                      : "Leave blank to book it in your own name."}
                  </small>
                </div>
              )}
              {!isDirectConfirm && (
                <div className="col-md-4 mb-3 d-flex align-items-end">
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="existing-customer-toggle"
                      checked={isExistingCustomer}
                      onChange={(e) => setIsExistingCustomer(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="existing-customer-toggle">Existing customer</label>
                  </div>
                </div>
              )}
            </div>
            <div className="row">
              {isExistingCustomer ? (
                <div className="col-md-8 mb-3">
                  <label className="form-label">Customer</label>
                  <CustomerPicker value={customerOption} onChange={setCustomerOption} />
                </div>
              ) : null}
              {/* Quotation cross-link (Batch164): pick one of this customer's
                  open quotations; it is linked the moment this is saved. */}
              {isExistingCustomer && canLinkQuotation && (
                <div className="col-md-4 mb-3">
                  <label className="form-label">Link Quotation (optional)</label>
                  <select
                    className="form-control"
                    value={linkQuotationGuid}
                    onChange={(e) => setLinkQuotationGuid(e.target.value)}
                    disabled={!pickedCustomer || isFetchingQuotations}
                  >
                    <option value="">
                      {!pickedCustomer
                        ? "Select the customer first"
                        : isFetchingQuotations
                          ? "Loading..."
                          : quotationOptions.length
                            ? "None"
                            : "No open quotations for this customer"}
                    </option>
                    {quotationOptions.map((q) => (
                      <option key={q.recordGuid} value={q.recordGuid}>
                        {q.quotationNumber} · {new Date(q.quotationDate).toLocaleDateString()} · {Number(q.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} · {QUOTATION_STATUS_LABEL[q.status] || q.status}
                        {q.salesReferenceNumber ? ` · now on ${q.salesReferenceNumber}` : ""}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">Linked when you {isDirectConfirm ? "confirm the order" : "submit"}.</small>
                </div>
              )}
              {!isExistingCustomer && (
                <>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Customer Name</label>
                    <input type="text" className="form-control" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Phone</label>
                    <input type="text" className="form-control" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Email (optional)</label>
                    <input type="email" className="form-control" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Address (optional)</label>
                    <input type="text" className="form-control" value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Area</label>
                    <input type="text" className="form-control" value={area} onChange={(e) => setArea(e.target.value)} />
                    <small className="text-muted">Used as the new customer's Location if this enquiry is later confirmed.</small>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Services & Pricing on the left, VAT & Discount + the grand total on
            the right (Direct Confirmed Order only - an enquiry is not priced to
            a final total here), so the total stays in view while lines are typed. */}
        <div className="row g-4 mb-4 align-items-start">
        <div className={isDirectConfirm ? "col-xl-8" : "col-12"}>
        <div className="card mb-0">
          <div className="card-body">
            <div className="d-flex flex-wrap justify-content-between align-items-baseline mb-2">
              <h5 className="mb-0">Services &amp; Pricing</h5>
              <small className="text-muted">
                {isDirectConfirm
                  ? "Tick each service and enter its quantity and rate."
                  : "Tick each service; quantity and rate are your proposal - the approver can adjust them."}
              </small>
            </div>
            {!branchId && <div className="text-muted small mb-3">Select a branch to see its service catalog.</div>}
            {/* A failed catalog load and a branch with no services look
                identical otherwise - the grid is simply blank and the form
                cannot be completed, with nothing on screen saying why. No
                permission is named here: whether this account may read the
                catalog is not a question this form asks, and never should be
                (see the Service Types master's own note). */}
            {branchId && serviceTypesFailed && (
              <div className="text-danger small mb-3">
                Could not load this branch&apos;s service catalog, so no service can be picked below.
              </div>
            )}
            {branchId && !serviceTypesFailed && (
              <div className="mb-3">
                <ServiceLinesGrid
                  serviceTypes={serviceTypeOptions}
                  value={serviceLines}
                  onChange={setServiceLines}
                  guardsQty={guardsCount}
                  emptyHint="This branch has no active service types set up yet."
                />
              </div>
            )}

            <div className="row mt-1">
              <div className="col-md-12">
                <label className="form-label">Remarks (optional)</label>
                <textarea className="form-control" rows={2} value={salespersonRemarks} onChange={(e) => setSalespersonRemarks(e.target.value)} />
              </div>
            </div>
          </div>
        </div>
        </div>

        {isDirectConfirm && (
          <div className="col-xl-4">
            {/* Sticks beside the grid while scrolling, so the total is always visible. */}
            <div className="card mb-0" style={{ position: "sticky", top: 90, borderTop: "3px solid #1E8449" }}>
              <div className="card-body">
                <h5 className="mb-3">VAT &amp; Discount</h5>
                <div className="row">
                  <div className="col-12 mb-3">
                    <label className="form-label">VAT %</label>
                    <input type="number" className="form-control" value={vatRate} onChange={(e) => setVatRate(e.target.value)} />
                  </div>
                  <DiscountFields
                    percent={discountPercent}
                    amount={discountAmount}
                    onPercentChange={setDiscountPercent}
                    onAmountChange={setDiscountAmount}
                    colClass="col-6"
                  />
                </div>
                <TotalsSummary totals={previewTotals} vatLabel={`VAT (${Number(vatRate) || 0}%)`} percent={discountPercent} />
              </div>
            </div>
          </div>
        )}
        </div>

        {guardsTicked && (
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="mb-3">Guard Requirements</h5>
              {/* The editor carries its own "Add Line" button in every state,
                  empty included. Its Qty total is the Guards line's quantity
                  in the grid above; the rate is typed once, on that line. */}
              <GuardRequirementsEditor rows={guardRows} onChange={setGuardRows} showRate={false} />
              {deploymentDate && (
                <small className="text-muted d-block mt-2">
                  Deployment date: <strong>{deploymentDate.split("-").reverse().join("/")}</strong> (earliest Start Date above)
                </small>
              )}
            </div>
          </div>
        )}

        <div className="d-flex gap-2 mb-4">
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={isSaving}>
            {isDirectConfirm ? "Confirm Order" : "Submit"}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(isDirectConfirm ? paths.salesOrders : paths.salesEnquiries)}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnquiryForm;
